/* eslint-disable react-hooks/exhaustive-deps */
import CalendarContext from "components/Calendar/CalendarContext";
import React, { useState, useEffect, useContext } from "react";

import { useDispatch } from "react-redux";
import { notifySuccess } from "redux/actions/Notifications";
import { Calendar as BigCalendar } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Event from "./Event";
import Grid from "@material-ui/core/Grid";
import { ConfirmDialog } from "components/common_components/Dialogs";
import { hasElements } from "utils/arrays";
import { calculateRange } from "components/Calendar/utils";
import Toolbar from "components/Calendar/components/Toolbar";
import Week from "components/Calendar/components/Week";
import Agenda from "components/Calendar/components/Agenda";
import { Paper } from "@material-ui/core";
import useStyles from "components/Calendar/useStyles";
import Header from "components/common_components/Header";
import { getCalendarLocalizer, isDate } from "utils/dateUtils";

const localizer = getCalendarLocalizer();
const DnDCalendar = withDragAndDrop(BigCalendar);

const GoogleCalendar = () => {
    const dispatch = useDispatch();

    const classes = useStyles();

    const [eventData, setEventData] = useState(undefined);
    const [openEvent, setOpenEvent] = useState(false);
    const [eventMode, setEventMode] = useState(undefined);

    const {
        date,
        setDate,
        view,
        setView,
        events,
        getEvents,
        resources,
        styleGetter,
        eventMappers,
        onGoingHandler,
        deleteEvent,
        loadResources,
        clearResources,
        saveEvent,
        onGuestsChange,
        guestCalendars,
        getEventAccessLevel,
        FiltersComponent
    } = useContext(CalendarContext);

    const [cancelConfirmDialogProps, setCancelConfirmDialogProps] = useState({
        open: false,
        title: "Discard",
        content: "Do you want to discard this event's changes?",
        onSecondaryClick: () => {},
        onPrimaryClick: () => {},
        onClose: () => {
            setCancelConfirmDialogProps((prev) => ({
                ...prev,
                open: false,
            }));
        },
    });

    useEffect(() => {
        loadResources?.();

        return () => {
            clearResources?.();
        };
    }, []);

    const getMyEvents = (range, userEmail) => {
        const { start, end } = range || calculateRange(view, date);
        if (isDate(start) && isDate(end)) {
            getEvents(start, end);
        }
    };

    const getEventsToShow = () => {
        let eventsToShow = events || [];

        if (hasElements(guestCalendars) && ["add", "edit"].includes(eventMode)) {
            eventsToShow = guestCalendars.reduce((arr, item) => {
                return [...arr, ...item.events.map((event) => ({ ...event, eventColor: item.color }))];
            }, eventsToShow);
        }
        let parsedEvents = eventsToShow.map(eventMappers.from);
        if (eventData) {
            if (eventMode === "add") {
                parsedEvents = [...parsedEvents, eventData];
            } else {
                const filteredEvents = parsedEvents.filter((x) => x.id !== eventData.id);
                parsedEvents = [...filteredEvents, eventData];
            }
        }
        return parsedEvents;
    };

    const onEventMoved = (data) => {
        const { start, end, event } = data;

        const isNewEvent = !Boolean(event.id);
        if (isNewEvent) {
            setEventData((prev) => ({ ...prev, start, end }));
            return;
        }

        if (!getEventAccessLevel(data.event).isWritter) return;

        const modifiedEvent = {
            ...event,
            startDateTime: start,
            endDateTime: end,
            guestEmails: hasElements(event.guests) && event.guests.map((g) => g.guestEmail),
        };

        saveEvent({
            mode: "edit",
            event: modifiedEvent,
            onSuccess: () => {
                dispatch(notifySuccess(`Event updated successfully!`));
                onEventSaved();
            },
        });
    };

    const onEventResize = (data) => {
        onEventMoved(data);
    };

    const onEventDrop = (data) => {
        onEventMoved(data);
    };

    const initializeNewEvent = (data) => {
        const { start, end } = data;
        setEventMode("add");
        setEventData(eventMappers.new(start, end));
        setOpenEvent(true);
    };

    const onSelectSlot = (data) => {
        if (eventData) {
            onCancelEventHandler(() => initializeNewEvent(data), false);
        } else {
            initializeNewEvent(data);
        }
    };

    const onSelectEvent = (data) => {
        if (data.id) {
            setEventMode("view");
        } else {
            setEventMode("add");
        }
        setEventData(data);
        setOpenEvent(true);
    };

    const onNavigate = (newDate, view, action) => {
        setDate(newDate);
    };

    const onView = (view) => {
        setView(view);
    };

    const onEventChangeHandler = (eventData) => {
        setEventData(eventData);
    };

    const restartEvent = (closeSideWindow = true) => {
        if (closeSideWindow) {
            setOpenEvent(false);
        }
        setEventMode(undefined);
        setEventData(undefined);
        onGuestsChange([]);
    };

    const onEventSaved = async () => {
        getMyEvents();
        restartEvent();
    };

    const onEditEventHandler = (data) => {
        setEventMode("edit");
        setEventData(data);
        setOpenEvent(true);
    };

    const onDeleteEvent = async (event) => {
        await deleteEvent(event);
        restartEvent();
        getMyEvents();
    };

    const onGoing = async (guestId, answer, comment, event) => {
        await onGoingHandler(guestId, answer, comment, event);
        restartEvent();
        getMyEvents();
    };

    const onCancelEventHandler = (primaryClickCallback, closeSideWindow = true) => {
        const executeCallback = () =>
            setTimeout(() => primaryClickCallback && typeof primaryClickCallback === "function" && primaryClickCallback(), 100);

        if (eventMode === "view") {
            restartEvent(closeSideWindow);
            executeCallback();
        } else {
            setCancelConfirmDialogProps((prev) => ({
                ...prev,
                open: true,
                onSecondaryClick: () => {
                    setCancelConfirmDialogProps((prev) => ({ ...prev, open: false }));
                },
                onPrimaryClick: () => {
                    restartEvent(closeSideWindow);
                    setCancelConfirmDialogProps((prev) => ({ ...prev, open: false }));
                    executeCallback();
                },
            }));
        }
    };

    return (
        <div>
            <Header title="Master Calendar" />

            <Grid container spacing={3} className={classes.container}>
                {openEvent && (
                    <Grid item xs={4} className="card">
                        <Event
                            data={eventData}
                            onChange={onEventChangeHandler}
                            mode={eventMode}
                            onCancel={onCancelEventHandler}
                            resources={resources}
                            onSuccessCallback={onEventSaved}
                            viewModeActionHandlers={{
                                onEdit: onEditEventHandler,
                                onDelete: onDeleteEvent,
                                onClose: restartEvent,
                                onGoing: (...params) => onGoing(...params, eventData),
                                onNotify: () => {},
                            }}
                        />
                    </Grid>
                )}
                <Grid item xs={openEvent ? 8 : 12} className="card">
                    <Paper className={classes.paper} variant="elevation">
                        <DnDCalendar
                            defaultDate={date}
                            defaultView={view}
                            views={["day", "week", "month", "agenda"]}
                            events={getEventsToShow()}
                            localizer={localizer}
                            onEventDrop={onEventDrop}
                            onEventResize={onEventResize}
                            onSelectSlot={onSelectSlot}
                            onSelectEvent={onSelectEvent}
                            onNavigate={onNavigate}
                            onView={onView}
                            resizable
                            selectable
                            style={{ height: "90vh" }}
                            eventPropGetter={styleGetter}
                            components={{
                                toolbar: Toolbar,
                                week: {
                                    header: Week.Header,
                                },
                                agenda: {
                                    date: Agenda.Header,
                                },
                            }}
                        />
                    </Paper>
                </Grid>
                {!openEvent && (
                    <Grid item xs={4} className="card">
                        <FiltersComponent />
                    </Grid>
                )}
                <ConfirmDialog {...cancelConfirmDialogProps} />
            </Grid>
        </div>
    );
};

export default GoogleCalendar;
